import dayjs from 'dayjs';
import {
  IncidenceRequestActor,
  IncidenceRequestDetailRecord,
  IncidenceRequestHistoryRecord,
  IncidenceRequestRhVisibleStatus,
  IncidenceRequestRecord,
  IncidenceRequestStatus,
  incidenceRequestRhVisibleStatusMap,
  incidenceRequestStatusMap,
} from 'types/incidenceRequests';

export type EmployeeIncidenceRequest = {
  id: string;
  folio: string;
  incidenceType: string;
  incidenceCategoryId: string;
  startDate: string;
  endDate: string;
  requestDate: string;
  updatedAt: string;
  status: IncidenceRequestStatus;
  rhVisibleStatus?: IncidenceRequestRhVisibleStatus;
  reason: string;
  rejectionReason?: string;
  attachments: Array<{
    name: string;
    url: string;
    s3Key?: string;
    mimeType?: string;
    size?: number;
  }>;
};

export type PendingApprovalIncidenceRequest = EmployeeIncidenceRequest & {
  requester: IncidenceRequestPerson;
  approvedAt?: string;
  authorizedAt?: string;
};

export type IncidenceRequestDetailContext =
  | 'mine'
  | 'approvals'
  | 'authorizations';

export type IncidenceRequestPerson = {
  id: string;
  employeeNumber: string;
  fullName: string;
  email: string;
  role?: string;
};

export type EmployeeIncidenceRequestHistoryItem = {
  id: string;
  folio: string;
  incidenceType: string;
  startDate: string;
  endDate: string;
  status: IncidenceRequestStatus;
};

export type EmployeeIncidenceRequestStatusEvent = {
  id: string;
  action: string;
  performedAt: string;
  fromStatus: IncidenceRequestStatus;
  toStatus: IncidenceRequestStatus;
  comment?: string;
  performedBy?: IncidenceRequestPerson;
};

export type EmployeeIncidenceRequestDetail = EmployeeIncidenceRequest & {
  requester: IncidenceRequestPerson;
  currentApprover?: IncidenceRequestPerson;
  approvedBy?: IncidenceRequestPerson;
  rejectedBy?: IncidenceRequestPerson;
  authorizedBy?: IncidenceRequestPerson;
  history: EmployeeIncidenceRequestHistoryItem[];
  statusEvents: EmployeeIncidenceRequestStatusEvent[];
};

export const editableRequestStatuses: IncidenceRequestStatus[] = [
  'draft',
  'rejected',
];

export const statusLabels: Record<IncidenceRequestStatus, string> = {
  draft: 'Borrador',
  review_hr: 'En revisión por RRHH',
  review_supervisor: 'En revisión por Supervisor',
  wrong_supervisor: 'Supervisor incorrecto',
  approved: 'Autorizada',
  rejected: 'Rechazada',
};

export const rhVisibleStatusLabels: Record<
  IncidenceRequestRhVisibleStatus,
  string
> = {
  approved_by_supervisor: 'Aprobado por Supervisor',
  supervisor_incorrect: 'Supervisor incorrecto',
};

type StatusPresentation = {
  label: string;
  tone: IncidenceRequestStatus;
};

export const allowedAttachmentMimeTypes = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'video/mp4',
] as const;

export const allowedAttachmentExtensions = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.mp4',
];

export const formatDisplayDate = (value?: string) =>
  value
    ? dayjs(value).locale('es').format('DD/MMM/YYYY').replace('.', '')
    : '-';

const normalizeCalendarDate = (value: string) => {
  const calendarDateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
  return calendarDateMatch ? calendarDateMatch[0] : value;
};

const mapActor = (
  actor?: IncidenceRequestActor | null
): IncidenceRequestPerson | undefined =>
  actor
    ? {
        id: actor._id,
        employeeNumber: actor.idEmployee,
        fullName: `${actor.name} ${actor.lastName}`.trim(),
        email: actor.email,
        role: actor.role,
      }
    : undefined;

export const mapIncidenceRequestRecord = (
  record: IncidenceRequestRecord
): EmployeeIncidenceRequest => ({
  id: record._id,
  folio: record.folio,
  incidenceType: `${record.incidenceCategory.abbr} - ${record.incidenceCategory.desc}`,
  incidenceCategoryId: record.incidenceCategory._id,
  startDate: normalizeCalendarDate(record.startDate),
  endDate: normalizeCalendarDate(record.endDate),
  requestDate: record.submittedAt ?? record.createdAt,
  updatedAt: record.updatedAt,
  status: incidenceRequestStatusMap[record.status],
  rhVisibleStatus: record.rhVisibleStatus
    ? incidenceRequestRhVisibleStatusMap[record.rhVisibleStatus]
    : undefined,
  reason: record.reason,
  rejectionReason: record.rejectionReason,
  attachments:
    record.attachments?.map((attachment) => ({
      name: attachment.name,
      url: attachment.url,
      s3Key: attachment.s3Key,
      mimeType: attachment.mimeType,
      size: attachment.size,
    })) ?? [],
});

export const mapPendingApprovalIncidenceRequestRecord = (
  record: IncidenceRequestRecord
): PendingApprovalIncidenceRequest => ({
  ...mapIncidenceRequestRecord(record),
  requester:
    mapActor(record.requester) ??
    ({
      id: '',
      employeeNumber: '-',
      fullName: 'Sin solicitante',
      email: '-',
    } as IncidenceRequestPerson),
  approvedAt: record.approvedAt,
  authorizedAt: record.authorizedAt,
});

const mapHistoryRecord = (
  record: IncidenceRequestHistoryRecord
): EmployeeIncidenceRequestHistoryItem => ({
  id: record._id,
  folio: record.folio,
  incidenceType: `${record.incidenceCategory.abbr} - ${record.incidenceCategory.desc}`,
  startDate: normalizeCalendarDate(record.startDate),
  endDate: normalizeCalendarDate(record.endDate),
  status: incidenceRequestStatusMap[record.status],
});

export const mapIncidenceRequestDetailRecord = (
  record: IncidenceRequestDetailRecord
): EmployeeIncidenceRequestDetail => ({
  ...mapIncidenceRequestRecord(record),
  requester: mapActor(record.requester)!,
  currentApprover: mapActor(record.currentApprover),
  approvedBy: mapActor(record.approvedBy),
  rejectedBy: mapActor(record.rejectedBy),
  authorizedBy: mapActor(record.authorizedBy),
  history: record.history.map(mapHistoryRecord),
  statusEvents: record.statusHistory.map((event, index) => ({
    id: `${record._id}-${event.action}-${event.performedAt}-${index}`,
    action: event.action,
    performedAt: event.performedAt,
    fromStatus: incidenceRequestStatusMap[event.fromStatus],
    toStatus: incidenceRequestStatusMap[event.toStatus],
    comment: event.comment,
    performedBy: mapActor(event.performedBy),
  })),
});

export const isEditableRequestStatus = (status: IncidenceRequestStatus) =>
  editableRequestStatuses.includes(status);

export const getRequestStatusPresentation = ({
  status,
  rhVisibleStatus,
  useRhVisibleStatus = false,
}: {
  status: IncidenceRequestStatus;
  rhVisibleStatus?: IncidenceRequestRhVisibleStatus;
  useRhVisibleStatus?: boolean;
}): StatusPresentation => {
  if (useRhVisibleStatus && rhVisibleStatus) {
    return rhVisibleStatus === 'approved_by_supervisor'
      ? {
          label: rhVisibleStatusLabels.approved_by_supervisor,
          tone: 'review_hr',
        }
      : {
          label: rhVisibleStatusLabels.supervisor_incorrect,
          tone: 'wrong_supervisor',
        };
  }

  return {
    label: statusLabels[status],
    tone: status,
  };
};

export const getStatusEventPresentation = ({
  action,
  fromStatus,
  toStatus,
  useRhVisibleStatus = false,
}: {
  action?: string;
  fromStatus: IncidenceRequestStatus;
  toStatus: IncidenceRequestStatus;
  useRhVisibleStatus?: boolean;
}): StatusPresentation => {
  if (action === 'REASSIGNED_SUPERVISOR') {
    return {
      label: 'Supervisor reasignado por RH',
      tone:
        toStatus === 'review_supervisor'
          ? 'review_supervisor'
          : toStatus === 'wrong_supervisor'
            ? 'wrong_supervisor'
            : 'review_hr',
    };
  }

  if (useRhVisibleStatus) {
    if (toStatus === 'wrong_supervisor') {
      return {
        label: rhVisibleStatusLabels.supervisor_incorrect,
        tone: 'wrong_supervisor',
      };
    }

    if (toStatus === 'review_hr' && fromStatus !== 'review_hr') {
      return fromStatus === 'review_supervisor'
        ? {
            label: rhVisibleStatusLabels.approved_by_supervisor,
            tone: 'review_hr',
          }
        : {
            label: rhVisibleStatusLabels.supervisor_incorrect,
            tone: 'wrong_supervisor',
          };
    }
  }

  return {
    label: statusLabels[toStatus],
    tone: toStatus,
  };
};

export const contextLabels: Record<IncidenceRequestDetailContext, string> = {
  mine: 'Mis solicitudes',
  approvals: 'Aprobaciones',
  authorizations: 'Autorizaciones',
};

export const shouldShowSteps = (context: IncidenceRequestDetailContext) =>
  context === 'mine';
